import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ClipboardList, Search, UserCheck, CalendarCheck, MessageSquare, Sparkles, ArrowRight, MapPin, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEPS = [
  { icon: ClipboardList, t: "Tell Us What You Need" }, { icon: Search, t: "Complete Intake" },
  { icon: UserCheck, t: "Explore Care Options" }, { icon: Sparkles, t: "Get Matched" },
  { icon: CalendarCheck, t: "Schedule" }, { icon: MessageSquare, t: "Stay Connected" },
];

export default function Families() {
  const navigate = useNavigate();
  return (
    <div>
      <section className="bg-blue-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="text-xs uppercase tracking-[0.16em] text-teal-400 font-semibold mb-3">For Families</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold max-w-2xl leading-tight">Find trusted care and manage your family's journey.</h1>
          <p className="mt-4 text-blue-100/80 max-w-2xl text-lg">From discovery to scheduling to staying connected — T5 makes coordinating care for your loved one simple and reassuring.</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10">
        <div className="rounded-3xl bg-white border border-blue-900/5 shadow-xl p-8">
          <h2 className="font-display text-2xl font-bold text-blue-950 text-center">Find care in minutes</h2>
          <div className="mt-6 grid sm:grid-cols-4 gap-3">
            <div className="relative"><MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input placeholder="ZIP Code" data-testid="fam-zip" className="pl-9 h-12" /></div>
            <Select><SelectTrigger className="h-12" data-testid="fam-type"><SelectValue placeholder="Type of Care" /></SelectTrigger><SelectContent>{["Personal Care", "Companion Care", "Respite Care", "Meal Prep", "Transportation"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            <Select><SelectTrigger className="h-12" data-testid="fam-sched"><SelectValue placeholder="Schedule" /></SelectTrigger><SelectContent>{["A few hours/week", "Daily", "Overnight", "24/7"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            <Input type="date" className="h-12" data-testid="fam-start" />
          </div>
          <div className="text-center mt-6"><Button onClick={() => navigate("/marketplace")} data-testid="fam-explore" className="h-12 px-8 bg-teal-600 hover:bg-teal-700 text-white gap-2">Explore Care Options <ArrowRight className="w-4 h-4" /></Button></div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-display text-3xl font-bold text-blue-950 text-center mb-10">Your care journey</h2>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {STEPS.map((s, i) => (
            <motion.div key={s.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="bg-white rounded-2xl p-5 border border-blue-900/5 shadow-sm text-center">
              <span className="grid place-items-center w-12 h-12 mx-auto rounded-xl bg-teal-50 text-teal-700 mb-3"><s.icon className="w-6 h-6" /></span>
              <p className="text-xs font-semibold text-blue-950">{i + 1}. {s.t}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="rounded-3xl bg-teal-600 p-10 lg:p-14 text-center text-white">
          <h2 className="font-display text-3xl font-bold">Ready to find care for your loved one?</h2>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Button onClick={() => navigate("/request-care")} className="h-12 px-7 bg-white text-teal-700 hover:bg-teal-50">Request Care</Button>
            <Button onClick={() => navigate("/marketplace")} variant="outline" className="h-12 px-7 border-white/60 text-white bg-white/10 hover:bg-white/20">Browse Caregivers</Button>
          </div>
        </div>
      </section>
    </div>
  );
}
